import React, { useState, useEffect, useRef } from 'react';
import { Controller, Path } from 'react-hook-form';
import Select, { components } from 'react-select';
import { OaFieldProps } from '../../types/OaForm';
import ValidationMessage from '../forms/ValidationMessage';

export interface SelectOptions<T> {
    label: string,
    value: T
}

interface InputSelectProps<T> extends OaFieldProps<T> {
    className?: string,
    options: SelectOptions<T>[],
    onSearchChange?: (e: string) => void,
    noOptionMessage?: string
    errorMessage?: string
    menuPosition?: 'static' | 'absolute'
    customOption?: any
    customSingleValue?: any
    // Add new optional prop to enable filter mode
    filterMode?: boolean
}

/**
 * Input Select component.
 * Enhanced to prevent focus loss in filter mode while maintaining all existing functionality.
 * @param param0 
 * @returns 
 */
export const InputSelect = <T,>({
    name,
    placeholder,
    methods,
    readonly,
    options,
    value,
    errorMessage,
    onSearchChange,
    onChange,
    noOptionMessage,
    menuPosition,
    customOption,
    customSingleValue,
    filterMode = false
}: InputSelectProps<T>): React.FunctionComponentElement<T> => {
    /**
     * Managed state value for this input component.
     */
    const [currentValue, setValue] = useState<T | undefined | null>(value);
    const [currentSearchValue, setSearchValue] = useState<string>('');
    
    /**
     * For filter mode: track external value changes to prevent unnecessary updates
     */
    const isInitialRender = useRef(true);
    const lastPropValue = useRef<T | undefined | null>(value);

    useEffect(() => {
        let tempValue;

        // Check whether the parent component set a value to value prop.
        if (value !== null && value !== undefined) {
            tempValue = value;

            // In filter mode, be more careful about when to update internal state
            if (filterMode) {
                // Only update if this is initial render or if parent value changed externally (like clear filter)
                if (isInitialRender.current || lastPropValue.current !== value) {
                    setValue(tempValue);
                    lastPropValue.current = value;
                    isInitialRender.current = false;
                }
            } else {
                // Original behavior for form mode
                setValue(tempValue);
            }

            // Update the registered React Hook Form value.
            // Reference: https://stackoverflow.com/a/62243132/6477254.
            if (methods) {
                methods.setValue(name as Path<{ [key: string]: T }>, tempValue);
            }
        } else {
            setValue(undefined);
            if (filterMode) {
                lastPropValue.current = undefined;
            }
        }

    }, [value, filterMode]);

    useEffect(() => {
        if (methods && methods.formState.isDirty === false) {
            setValue(value ?? undefined);
        }
    }, [methods?.formState.isDirty]);

    const getBorderColor = () => {
        if (methods?.formState.errors[name]) {
            // theme red
            return '#ff0000';
        }

        else if (methods?.formState.dirtyFields[name]) {
            // bootstrap green
            // return '#198754';
            return '#0e38b7'; // blue LSP
        }

        return 'lightgrey';
    }

    const getErrorMessage = () => {
        const errors = methods?.formState.errors[name];

        if (errors && 'message' in errors) {
            return errors['message'];
        }

        return ''
    }

    const onInputChange = async (e) => {
        setValue(e.value);
        
        // Update React Hook Form value when not using Controller
        if (methods && !methods.control) {
            methods.setValue(name as Path<{ [key: string]: T }>, e.value);
        }
        
        if (onChange && e) {
            onChange(e.value);
            
            // In filter mode, update the tracked value immediately
            if (filterMode) {
                lastPropValue.current = e.value;
            }
        }
    }

    const onSearchInputChange = async (e) => {
        setSearchValue(e);
        if (onSearchChange && e) {
            onSearchChange(e);
        }
    }

    const customStyles = {
        valueContainer: (base) => ({
            ...base,
            paddingTop: 3,
            paddingBottom: 3,
        }),
        menu: (provided) => ({
            ...provided,
            position: menuPosition ? menuPosition : 'absolute',
            zIndex: 9999
        }),
        dropdownIndicator: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
        }),
        control: (base) => (
            methods ? {
                ...base,
                '&:hover': { borderColor: getBorderColor() },
                borderColor: getBorderColor(),
                boxShadow: 'none !important',
                minHeight: 'unset',
                fontSize: 14
            } : {
                ...base,
                minHeight: 'unset',
            }
        )
    };


    return (
        <React.Fragment>
            {/* this component can only be rendered inside OaForm or if methods prop present */}
            {methods
                ? <Controller
                    control={methods?.control}
                    // this is not an error in typescript 4.3.5
                    name={name as Path<{ [key: string]: T }>}
                    render={({ field }) => (
                        <Select
                            instanceId={name as Path<{ [key: string]: T }>}
                            isDisabled={readonly}
                            options={options as SelectOptions<typeof field.value>[]}

                            selected={field.value}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            onChange={(option) => {
                                field.onChange(option?.value);
                                onInputChange(option);
                            }}
                            className={getBorderColor()}

                            value={
                                {
                                    label: options.find(Q => Q.value === field.value)?.label ?? placeholder,
                                    value: field.value
                                }
                            }

                            styles={customStyles}
                            noOptionsMessage={() => noOptionMessage ?? "No Option"}
                            components={{
                                Option: customOption || components.Option,
                                SingleValue: customSingleValue || components.SingleValue
                            }}
                        />
                    )}
                />

                : <Select
                    instanceId={name}
                    isDisabled={readonly}
                    options={options}
                    components={(currentSearchValue === '') && onSearchChange ?
                        {
                            ...components,
                            Option: customOption || components.Option,
                            SingleValue: customSingleValue || components.SingleValue,

                            Menu: () => null
                        } :
                        {
                            ...components,
                            Option: customOption || components.Option,
                            SingleValue: customSingleValue || components.SingleValue
                        }
                    }
                    onInputChange={(e) => { onSearchInputChange(e) }}
                    selected={currentValue}
                    placeholder={placeholder}
                    onChange={(option) => onInputChange(option)}
                    styles={customStyles}
                    value={currentValue !== undefined ? {
                        label: options.find(Q => Q.value === currentValue)?.label,
                        value: currentValue
                    } : ''}
                    noOptionsMessage={() => noOptionMessage ?? "No Option"}
                />
            }

            <ValidationMessage className="small text-danger" message={getErrorMessage()} />

            {
                errorMessage &&
                <p className="text-danger small">{errorMessage}</p>
            }
        </React.Fragment>);
}